// Client-side image quality validation. We run the same checks for both
// drag/drop uploads and live camera captures so the applicant gets fast
// feedback BEFORE wasting a network round-trip to the verification pipeline. The math is
// kept on a 256-pixel-wide downscale so it stays free even on low-end phones.
//
// Sandbox mode bypasses the blur/brightness heuristics because deterministic
// test fixtures (synthetic 1024x768 PNGs etc.) are often perfectly flat and
// would trip the variance check, defeating the point of sandbox.

export const QUALITY_RULES = {
  minBytes: 20 * 1024,            // 20 KB
  maxBytes: 8 * 1024 * 1024,      // 8 MB
  minWidth: 600,
  minHeight: 400,
  minLuminance: 30,               // mean grey (0-255). Below = too dark
  maxLuminance: 230,              // above = blown out / glare
  minSharpness: 50,               // Laplacian variance threshold
};

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode_failed')); };
    img.src = url;
  });
}

// Compute mean luminance + Laplacian variance (a cheap blur metric).
// Returns { mean, variance } in 0..255 units.
function analyseLuma(imageData) {
  const { data, width, height } = imageData;
  const lum = new Float32Array(width * height);
  let sum = 0;
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    const y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    lum[j] = y;
    sum += y;
  }
  const mean = sum / lum.length;
  // 5-point Laplacian: -L_up -L_left +4L -L_right -L_down
  let lapSum = 0, lapSqSum = 0, count = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const v = -lum[i - width] - lum[i - 1] + 4 * lum[i] - lum[i + 1] - lum[i + width];
      lapSum += v;
      lapSqSum += v * v;
      count++;
    }
  }
  const lapMean = lapSum / count;
  const variance = lapSqSum / count - lapMean * lapMean;
  return { mean, variance };
}

export async function validateImage(file, { sandbox = false } = {}) {
  if (!file) return { ok: false, error: 'No file selected.' };
  if (file.size < QUALITY_RULES.minBytes) {
    return { ok: false, error: `Image is too small (${(file.size / 1024).toFixed(0)} KB). Use a clearer photo of at least 20 KB.` };
  }
  if (file.size > QUALITY_RULES.maxBytes) {
    return { ok: false, error: 'Image is larger than 8 MB. Please reduce its size.' };
  }
  let img;
  try { img = await loadImage(file); }
  catch { return { ok: false, error: 'Could not read this image. Try another file or format (JPG/PNG/WEBP).' }; }

  if (img.width < QUALITY_RULES.minWidth || img.height < QUALITY_RULES.minHeight) {
    return {
      ok: false,
      error: `Image is too small (${img.width}×${img.height}). Need at least ${QUALITY_RULES.minWidth}×${QUALITY_RULES.minHeight}.`,
    };
  }

  // Sandbox: stop after structural checks - heuristics misbehave on synthetic test files.
  if (sandbox) return { ok: true, metrics: { width: img.width, height: img.height } };

  // Downscale to 256 px wide (preserve aspect) to keep the analysis cheap on phones.
  const targetW = 256;
  const scale = targetW / img.width;
  const targetH = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = targetW; canvas.height = targetH;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, targetW, targetH);
  const imageData = ctx.getImageData(0, 0, targetW, targetH);
  const { mean, variance } = analyseLuma(imageData);

  if (mean < QUALITY_RULES.minLuminance) {
    return { ok: false, error: 'Image is too dark. Take the photo in better light.' };
  }
  if (mean > QUALITY_RULES.maxLuminance) {
    return { ok: false, error: 'Image is overexposed. Reduce glare or turn off flash.' };
  }
  if (variance < QUALITY_RULES.minSharpness) {
    return {
      ok: false,
      error: `Image looks blurry (sharpness ${variance.toFixed(0)}). Hold steady and try again.`,
    };
  }
  return { ok: true, metrics: { width: img.width, height: img.height, mean: +mean.toFixed(1), variance: +variance.toFixed(1) } };
}
